/**
 * Batched rail fetching for progressive/lazy loading
 *
 * @remarks
 * This module provides functionality to load rails in batches rather than all at once.
 * On the first call, it fetches all rail metadata (cheap), then progressively loads
 * tile data for each batch as needed.
 */

import {
  RailWithTiles,
  FetchBrowsePageOptions,
  isGameRail,
  isPersonalizedRail,
} from "./types";

import { fetchAssetDetails } from "@/utils/FetchAssetDetails";
import {
  isCollection,
  isLink,
  AssetResponse,
} from "@/utils/FetchAssetDetails/types";
import { fetchChildItems } from "@/utils/FetchChildItems";
import { PeacockNodeType } from "@/utils/shared-atom-types";

interface FetchBrowsePageBatchedOptions extends FetchBrowsePageOptions {
  startIndex: number;
  batchSize: number;
  allRails?: RailWithTiles[];
}

interface FetchBrowsePageBatchedResult {
  loadedRails: RailWithTiles[];
  allRails: RailWithTiles[];
  hasMore: boolean;
}

/**
 * Fetches a batch of rails with their tiles
 *
 * @remarks
 * On first call (when allRails is not provided):
 * 1. Fetches the browse page group
 * 2. Fetches all rail metadata (lightweight)
 * 3. Loads tiles for the first batch only
 *
 * On subsequent calls (when allRails is provided):
 * 1. Uses the cached rail metadata
 * 2. Loads tiles for the next batch
 *
 * @param slug - The browse page slug
 * @param options - Configuration including batch settings
 * @returns Promise with loaded rails, all rails metadata, and hasMore flag
 */
export const fetchBrowsePageBatched = async (
  slug: string,
  options: FetchBrowsePageBatchedOptions,
): Promise<FetchBrowsePageBatchedResult> => {
  const {
    fetchTileDetails = false,
    skipPersonalized = true,
    showMobileContent = true,
    startIndex,
    batchSize,
    allRails: providedAllRails,
  } = options;

  let allRails: RailWithTiles[] = [];

  /** First call: fetch all rail metadata */
  if (!providedAllRails) {
    const browsePage = await fetchAssetDetails(slug);

    if (browsePage.type !== PeacockNodeType.Group) {
      throw new Error(
        `Expected ${PeacockNodeType.Group} but got ${browsePage.type} for ${slug}`,
      );
    }

    /** Fetch all collections (rails) metadata only */
    const railsResponse = await fetchChildItems(browsePage.id);
    const rails = railsResponse.items;

    /** Create rail objects with metadata but no tiles yet */
    allRails = rails.map((rail) => {
      const railType = rail.type;
      const title = rail.attributes?.title || "Untitled Rail";
      const railSlug = rail.attributes?.slug || "";
      const itemsCount = rail.childTypes?.items?.count || 0;

      const renderHint =
        isCollection(rail) || isLink(rail)
          ? rail.attributes.renderHint
          : undefined;
      const hasLinkId =
        isCollection(rail) || isLink(rail) ? rail.attributes.linkId : undefined;
      const isPersonalized =
        railType === PeacockNodeType.Link ||
        (railType === PeacockNodeType.Collection && hasLinkId);

      return {
        ...rail,
        title,
        slug: railSlug,
        itemsCount,
        tiles: [],
        renderHint: renderHint || {},
        isPersonalized,
        linkId: hasLinkId || undefined,
      } as RailWithTiles;
    });

    /** Filter rails based on options */
    allRails = allRails.filter((rail) => {
      /** Filter out personalized rails if skipPersonalized is true */
      if (skipPersonalized && isPersonalizedRail(rail)) {
        return false;
      }

      /** Filter out mobile content if needed */
      if (!showMobileContent) {
        const contentLevel = rail.contentLevel;
        if (
          (contentLevel &&
            typeof contentLevel === "string" &&
            (contentLevel.includes("CLIP") ||
              contentLevel.includes("STORY"))) ||
          isGameRail(rail)
        ) {
          return false;
        }
      }

      return true;
    });
  } else {
    allRails = providedAllRails;
  }

  /** Validate allRails contains expected data structure */
  if (!Array.isArray(allRails) || allRails.length === 0) {
    return {
      loadedRails: [],
      allRails: [],
      hasMore: false,
    };
  }

  /** Determine which rails to load in this batch */
  const endIndex = Math.min(startIndex + batchSize, allRails.length);
  const railsToLoad = allRails.slice(startIndex, endIndex);

  /** Load tiles for this batch */
  const loadedRails = await Promise.all(
    railsToLoad.map(async (rail) => {
      const railType = rail.type;

      /** Load tiles for this rail */
      if (
        railType === PeacockNodeType.Collection ||
        railType === PeacockNodeType.Link
      ) {
        try {
          /** Use rail.id to fetch child items */
          const tilesResponse = await fetchChildItems(rail.id);
          let tiles = tilesResponse.items;

          /** Optionally fetch full asset details for all tiles */
          let tilesWithDetails: AssetResponse[] = [];
          if (fetchTileDetails && tiles.length > 0) {
            const BATCH_SIZE = 20;
            tilesWithDetails = [];

            for (let i = 0; i < tiles.length; i += BATCH_SIZE) {
              const batch = tiles.slice(i, i + BATCH_SIZE);
              const batchResults = await Promise.all(
                batch.map(async (tile) => {
                  try {
                    const tileWithImageType = tile as typeof tile & {
                      imageType?: string;
                    };

                    if (tile.type === PeacockNodeType.Link) {
                      const linkDetails = await fetchAssetDetails(tile.id);
                      return tileWithImageType.imageType
                        ? {
                            ...linkDetails,
                            imageType: tileWithImageType.imageType,
                          }
                        : linkDetails;
                    }

                    if (tile.type === PeacockNodeType.Collection) {
                      const collectionDetails = await fetchAssetDetails(
                        tile.id,
                      );
                      return tileWithImageType.imageType
                        ? {
                            ...collectionDetails,
                            imageType: tileWithImageType.imageType,
                          }
                        : collectionDetails;
                    }

                    if (
                      tile.type === PeacockNodeType.Programme ||
                      tile.type === PeacockNodeType.Series ||
                      tile.type === PeacockNodeType.Season ||
                      tile.type === PeacockNodeType.Episode ||
                      tile.type === PeacockNodeType.SLE
                    ) {
                      const details = await fetchAssetDetails(tile.id);
                      return tileWithImageType.imageType
                        ? { ...details, imageType: tileWithImageType.imageType }
                        : details;
                    }

                    return tile;
                  } catch (error) {
                    return tile;
                  }
                }),
              );
              tilesWithDetails.push(...batchResults);
            }
          }

          return {
            ...rail,
            tiles,
            tilesWithDetails:
              tilesWithDetails.length > 0 ? tilesWithDetails : undefined,
          } as RailWithTiles;
        } catch (error) {
          return {
            ...rail,
            tiles: [],
            error: error instanceof Error ? error.message : String(error),
          } as RailWithTiles;
        }
      }

      return rail;
    }),
  );

  return {
    loadedRails,
    allRails,
    hasMore: endIndex < allRails.length,
  };
};
